import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, message, Select, Switch, Space } from 'antd';

import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { isEmpty as _isEmpty } from 'lodash-es';

import { hasFeature } from '../../../utils/restrictions';

import { showUpselling } from '../../../store/actions/paymentActions';
import { LANGUAGES, submitActionTexts, submitTextsDefault } from '../../../constants';

import SettingsFooter from '../SettingsFooter';
import PhoneInputFormItem from '../../PhoneInput';
import { getRootDomain } from '../../../utils/helpers';
import { Link } from 'react-router-dom';
const { Option } = Select;

const GeneralSettings = (props) => {
  const {
    reset,
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields, isDirty },
  } = useForm({
    defaultValues: {
      name: props?.form.name || '',
      link: props?.form.link || '',
      enabled: props?.form.enabled !== undefined ? props?.form.enabled : true,
      language: props?.form.language || 'en',
      whatsappNumber: props?.form.whatsappNumber || '',
      submitText: props?.form.submitText == null ? '' : props.form.submitText,
    },
  });
  const { t } = useTranslation();

  const watchLanguage = watch('language', 'en');

  const { me } = useSelector((state) => state.auth);

  props.dirtyFieldsRef.current = dirtyFields;

  useEffect(() => {
    reset({
      name: props?.form?.name || '',
      link:
        props?.customDomain?.hostname && props?.form?.customDomainLink
          ? props?.form?.customDomainLink
          : props?.form?.link,
      enabled: props?.form.enabled !== undefined ? props?.form.enabled : true,
      language: props?.form?.language || 'en',
      whatsappNumber: props?.form.whatsappNumber || '',
      submitText: props?.form.submitText == null ? '' : props.form.submitText,
    });
  }, [props.form, reset]);

  function handleSave(data) {
    props.handleDispatch(data);
  }

  function handleError(error) {
    message.error(t('formSettings.updateFail'));
  }

  const dispatch = useDispatch();

  return (
    <div className="form-settings-screen">
      <Form
        className="form-settings-inputs"
        colon={false}
        labelCol={{
          span: 8,
        }}
        labelAlign="left"
        requiredMark={false}
      >
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              label={t('formSettings.formName')}
              validateStatus={_isEmpty(error) ? 'success' : 'error'}
              help={!_isEmpty(error) ? error?.message : null}
            >
              <Input {...field} />
            </Form.Item>
          )}
          control={control}
          name="name"
          rules={{ required: t('formSettings.formNameRequired') }}
        />
        <Controller
          control={control}
          name="link"
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              label={t('formSettings.formLink')}
              style={{ marginBottom: '1rem' }}
              validateStatus={_isEmpty(error) ? 'success' : 'error'}
              help={
                _isEmpty(error) ? (
                  <span>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('formSettings.you-can-change-whatsform'),
                      }}
                    ></span>{' '}
                    <Link to="/account">{t('common.profile')}</Link>
                  </span>
                ) : (
                  error?.message || null
                )
              }
            >
              {/* <Space.Compact> */}
              {/* <Select defaultValue={`${getRootDomain(props.customDomain)}/`} /> */}
              <Input
                addonBefore={`${getRootDomain(props.customDomain)}/`}
                onChange={
                  !hasFeature(props.plan, 'customLink')
                    ? () => {
                        return dispatch(showUpselling('link'));
                      }
                    : field.onChange
                }
                value={field.value}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
              {/* </Space.Compact> */}
            </Form.Item>
          )}
          rules={{
            required: t('formSettings.formLinkRequired'),
            minLength: { value: 3, message: t('formSettings.formLinkMin') },
            maxLength: { value: 25, message: t('formSettings.formLinkMax') },
            pattern: { value: /^[a-zA-Z0-9_-]+$/, message: t('formSettings.formLinkPattern') },
          }}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <Form.Item label={t('create.whatsAppNo')} style={{ marginBottom: '0px' }}>
              <PhoneInputFormItem {...field} error={error} />
            </Form.Item>
          )}
          control={control}
          name="whatsappNumber"
          rules={{
            validate: (value) =>
              isPossiblePhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
          }}
        />

        <Controller
          control={control}
          name="language"
          render={({ field }) => (
            <Form.Item label={t('formSettings.language')}>
              <Select showSearch style={{ width: 120 }} {...field}>
                {LANGUAGES.map((l) => (
                  <Option key={l.code} value={l.code}>
                    {l.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        />

        <Controller
          control={control}
          name="submitText"
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              label={t('formSettings.submitButtonText')}
              validateStatus={_isEmpty(error) ? 'success' : 'error'}
              help={!_isEmpty(error) ? error?.message : null}
              className="input-addon-right"
            >
              <Input
                addonAfter={submitActionTexts[watchLanguage]}
                onChange={field.onChange}
                value={field.value}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                placeholder={submitTextsDefault[watchLanguage]}
              />
            </Form.Item>
          )}
        />
        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <Form.Item label={t('formSettings.enable')}>
              <Switch
                className="form-input"
                onChange={field.onChange}
                checked={field.value}
                name={field.name}
                ref={field.ref}
              />
            </Form.Item>
          )}
        />
        {!hasFeature(props.plan, 'removeBranding') && (
          <Form.Item label={t('formSettings.branding')}>
            <Switch
              className="form-input"
              onChange={() => {
                dispatch(showUpselling('branding'));
                return false;
              }}
              checked={true}
            />
          </Form.Item>
        )}
      </Form>
      <SettingsFooter
        handleSubmit={handleSubmit(handleSave, handleError)}
        loading={props.isLoading}
        isDirty={isDirty}
      />
    </div>
  );
};

export default GeneralSettings;
